import $ from 'jquery';
import { nodeConfig } from '@/mixins';
import { mod } from '@/helpers/utils';
import { Swap } from '@/plugins/swap';

const NAMESPACE = 'nav-reveal';
const DEFAULT_SELECTOR = '[data-js-nav-reveal-item]';
const DEFAULT_SETTINGS = {
	//
};

class NavReveal {
	constructor(node, config) {
		this.$node = $(node);
		this.config = config;

		this.$trigger = this.$node.find('[data-js-nav-reveal-toggle]');
		this.$children = this.$node.find('[data-js-nav-reveal-children]');
		this.$childLinks = this.$node.find('[data-js-nav-reveal-child-link]');
		this.$items = this.$node.find('[data-js-nav-reveal-child-item]');
	}

	get hasActive() {
		return this.$node.find('.state-swap-active').length;
	}

	/** @param {Swap} swap */
	onTriggerActivate(swap) {
		const targets = swap.targetNodes;

		targets.forEach((target) => {
			const revealChildren = target.querySelector('[data-js-nav-reveal-children]');
			revealChildren?.setAttribute('aria-hidden', 'false');
		});
	}

	/** @param {Swap} swap */
	onTriggerDeactivate(swap) {
		const targets = swap.targetNodes;

		targets.forEach((target) => {
			const revealChildren = target.querySelector('[data-js-nav-reveal-children]');
			revealChildren?.setAttribute('aria-hidden', 'true');
		});
	}

	focusFirst() {
		this.$items.first().find('[data-js-nav-reveal-child-link]').trigger('focus');
	}

	focusLast() {
		this.$items.last().find('[data-js-nav-reveal-child-link]').trigger('focus');
	}

	onTriggerKeydown(event) {
		let keyCode = event.key.toLowerCase();

		if (!['arrowup', 'arrowdown'].includes(keyCode)) return;

		let $focusedElement = $(':focus');

		event.preventDefault();

		if (!$(this).hasClass('state-swap-active')) {
			Swap.activate($focusedElement[0]);
		}

		let lookup = {
			arrowup: () => {
				this.focusLast();
			},
			arrowdown: () => {
				this.focusFirst();
			}
		};

		if (keyCode in lookup) {
			lookup[keyCode]();
		}
	}

	onTriggerClick() {
		if (!this.$node.hasClass('state-swap-active')) return;

		this.focusFirst();
	}

	onNavKeydown(event) {
		let keyCode = event.key.toLowerCase();

		if (!['tab', 'escape', 'home', 'end', 'arrowup', 'arrowdown'].includes(keyCode)) return;

		if (!['tab'].includes(keyCode)) event.preventDefault();

		let $focusedElement = $(':focus');
		let $focusedItem = $focusedElement.closest('[data-js-nav-reveal-child-item]');
		let focusedIndex = this.$items.index($focusedItem);

		let lookup = {
			tab: () => {
				const swap = $focusedElement[0].closest('[data-js-nav-reveal-item]').querySelector('[data-js-nav-reveal-toggle]');
				Swap.deactivate(swap);
			},
			escape: () => {
				const swap = $focusedElement[0].closest('[data-js-nav-reveal-item]').querySelector('[data-js-nav-reveal-toggle]');
				Swap.deactivate(swap);
				swap?.focus();
			},
			home: () => {
				this.focusFirst($focusedItem);
			},
			end: () => {
				this.focusLast($focusedItem);
			},
			arrowup: () => {
				let prevIndex = mod(focusedIndex - 1, this.$items.length);
				this.$items.eq(prevIndex).find('[data-js-nav-reveal-child-link]').trigger('focus');
			},
			arrowdown: () => {
				let nextIndex = mod(focusedIndex + 1, this.$items.length);
				this.$items.eq(nextIndex).find('[data-js-nav-reveal-child-link]').trigger('focus');
			}
		};

		if (keyCode in lookup) {
			lookup[keyCode]();
		}
	}

	bindUI() {
		this.$trigger.on({
			keydown: this.onTriggerKeydown.bind(this),
			click: this.onTriggerClick.bind(this)
		});

		Swap.on(this.$trigger[0], 'activate', this.onTriggerActivate.bind(this));
		Swap.on(this.$trigger[0], 'deactivate', this.onTriggerDeactivate.bind(this));

		this.$children.on('keydown', this.onNavKeydown.bind(this));
	}

	setup() {
		const swapConfig = {
			target: this.$node[0],
			triggerAttributes: [
				{
					attribute: 'aria-expanded',
					on: 'true',
					off: 'false'
				}
			],
		};

		if (this.config?.item?.group) {
			swapConfig.group = this.config.item.group;
		}

		new Swap(this.$trigger[0], swapConfig);

		this.$trigger.attr({
			'aria-haspopup': 'true'
		});

		this.$children.attr('aria-hidden', 'true');
		this.$childLinks.attr('tabindex', '-1');
	}

	init() {
		this.setup();
		this.bindUI();
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	let listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		let config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		let module = new NavReveal(node, config);

		module.init();

		return module;
	});
}
